/** Routes de l'application, déclarées en code : un seul fichier à lire pour connaître tous les écrans. */
import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { requeteMoi, requetes } from "./requetes";
import { Cadre } from "./ecrans/Cadre";
import { Connexion, Inscription, Invitation, MotDePasseOublie, Reinitialisation } from "./ecrans/Acces";
import { Accueil, MesDossiers } from "./ecrans/Accueil";
import { NouveauDossier } from "./ecrans/NouveauDossier";
import { EcranDossier } from "./ecrans/Dossier";
import { Formations, FicheFormation } from "./ecrans/Formations";
import { Outils } from "./ecrans/Outils";
import { Repertoire } from "./ecrans/Repertoire";
import { MaCandidature } from "./ecrans/Candidature";
import { AdminCandidatures, AdminOrganisme } from "./ecrans/Admin";
import { BoiteReception } from "./ecrans/BoiteReception";
import { Bpf, Courriers, Compte } from "./ecrans/Divers";
import { CoffreParcours, CoffresParcours } from "./ecrans/Coffre";
import { PagePositionnement, Positionnements } from "./ecrans/Positionnements";
import { PageFormulaire } from "./ecrans/Formulaire";
import { Archives } from "./ecrans/Archives";
import { PageIntrouvable } from "./ecrans/Introuvable";
import { ModeEmploi } from "./ecrans/ModeEmploi";
import { Pilotage } from "./ecrans/Pilotage";
import { IndicateursResultats } from "./ecrans/IndicateursResultats";
import { AdminVeille } from "./ecrans/Veille";
import { FichesFinanceurs, SuiviDemande, TableauFinancements } from "./ecrans/Financements";
import { AdminCpf } from "./ecrans/Cpf";
import { GalerieStyles } from "./ecrans/Charte";
import { SourcesFormation } from "./ecrans/Sources";
import { MaVitrine } from "./ecrans/MaVitrine";
import { AudienceSite } from "./ecrans/AudienceSite";
import { ArborescenceDossiers } from "./ecrans/ArborescenceDossiers";
import { EspaceAmbassadeur, PageRecommandation } from "./ecrans/Ambassadeur";
import { AdminAmbassadeurs, ReglagesPaiementsAmbassadeurs } from "./ecrans/AdminAmbassadeurs";

const racine = createRootRoute({ component: Outlet, notFoundComponent: PageIntrouvable });

// Accès public
const connexion = createRoute({
  getParentRoute: () => racine,
  path: "/connexion",
  component: Connexion,
  validateSearch: (s: Record<string, unknown>): { retour?: string } => (typeof s.retour === "string" ? { retour: s.retour } : {}),
});
const inscription = createRoute({
  getParentRoute: () => racine,
  path: "/inscription",
  component: Inscription,
  // Programme ambassadeur : `?parrain=CODE` (lien de parrainage d'un formateur).
  validateSearch: (s: Record<string, unknown>): { parrain?: string } => (typeof s.parrain === "string" ? { parrain: s.parrain } : {}),
});
// Programme ambassadeur : formulaire public de recommandation d'un apprenant (sans compte).
const recommandationPublique = createRoute({ getParentRoute: () => racine, path: "/recommandation/$code", component: PageRecommandation });
const invitation = createRoute({ getParentRoute: () => racine, path: "/invitation/$jeton", component: Invitation });
const motDePasseOublie = createRoute({ getParentRoute: () => racine, path: "/mot-de-passe-oublie", component: MotDePasseOublie });
const reinitialisation = createRoute({ getParentRoute: () => racine, path: "/reinitialisation/$jeton", component: Reinitialisation });
// Page publique de positionnement (« Modification 1 ») : accessible par le lien personnel reçu par e-mail, sans compte.
const positionnementPublic = createRoute({ getParentRoute: () => racine, path: "/positionnement/$jeton", component: PagePositionnement });
// Page publique d'un formulaire apprenant (version 7) : recueil, positionnement, acquis, satisfaction — par lien personnel ou QR code.
const formulairePublic = createRoute({ getParentRoute: () => racine, path: "/formulaire/$jeton", component: PageFormulaire });

// Application : tout ce qui suit passe par le cadre, qui exige une session.
const app = createRoute({
  getParentRoute: () => racine,
  id: "app",
  component: Cadre,
  // Garde d'accès, AVANT tout rendu : pas de session → connexion ; formateur non validé → sa candidature (F-ONB-02).
  beforeLoad: async ({ location }) => {
    const { acteur } = await requetes.ensureQueryData(requeteMoi);
    if (!acteur) throw redirect({ to: "/connexion", search: location.pathname === "/" ? {} : { retour: location.pathname } });
    if (acteur.role === "formateur" && !acteur.formateur_valide && !["/candidature", "/profil", "/compte"].includes(location.pathname)) throw redirect({ to: "/candidature" });
  },
});
const accueil = createRoute({ getParentRoute: () => app, path: "/", component: Accueil });
const mesDossiers = createRoute({ getParentRoute: () => app, path: "/dossiers", component: MesDossiers });
const nouveauDossier = createRoute({ getParentRoute: () => app, path: "/dossiers/nouveau", component: NouveauDossier });
// 04/11/2026 : arborescence Dossier › Apprenant › pièces (formateur, organisme).
const arborescence = createRoute({ getParentRoute: () => app, path: "/dossiers/arborescence", component: ArborescenceDossiers });
// 04/11/2026 : `?onglet=…&section=…` — les « éléments manquants » des cartes mènent au bon endroit du dossier.
const dossier = createRoute({
  getParentRoute: () => app,
  path: "/dossiers/$id",
  component: EcranDossier,
  validateSearch: (s: Record<string, unknown>): { onglet?: string; section?: string } => ({
    ...(typeof s.onglet === "string" ? { onglet: s.onglet } : {}),
    ...(typeof s.section === "string" ? { section: s.section } : {}),
  }),
});
const formations = createRoute({ getParentRoute: () => app, path: "/formations", component: Formations });
const formation = createRoute({ getParentRoute: () => app, path: "/formations/$id", component: FicheFormation });
const sourcesFormation = createRoute({ getParentRoute: () => app, path: "/formations/$id/sources", component: SourcesFormation });
const outils = createRoute({ getParentRoute: () => app, path: "/outils", component: Outils });
const repertoire = createRoute({ getParentRoute: () => app, path: "/repertoire", component: Repertoire });
const candidature = createRoute({ getParentRoute: () => app, path: "/candidature", component: MaCandidature });
const adminCandidatures = createRoute({ getParentRoute: () => app, path: "/admin/candidatures", component: AdminCandidatures });
const adminBoiteReception = createRoute({ getParentRoute: () => app, path: "/admin/boite-de-reception", component: BoiteReception });
const adminOrganisme = createRoute({ getParentRoute: () => app, path: "/admin/organisme", component: AdminOrganisme });
const bpf = createRoute({ getParentRoute: () => app, path: "/bpf", component: Bpf });
const courriers = createRoute({ getParentRoute: () => app, path: "/courriers", component: Courriers });
const compte = createRoute({ getParentRoute: () => app, path: "/compte", component: Compte });
const profil = createRoute({ getParentRoute: () => app, path: "/profil", component: MaCandidature });
const coffres = createRoute({ getParentRoute: () => app, path: "/coffres", component: CoffresParcours });
const coffre = createRoute({ getParentRoute: () => app, path: "/coffres/$id", component: CoffreParcours });
const positionnements = createRoute({ getParentRoute: () => app, path: "/positionnements", component: Positionnements });
const archives = createRoute({ getParentRoute: () => app, path: "/archives", component: Archives });
const modeEmploi = createRoute({ getParentRoute: () => app, path: "/mode-emploi", component: ModeEmploi });
const adminPilotage = createRoute({ getParentRoute: () => app, path: "/admin/pilotage", component: Pilotage });
const adminIndicateurs = createRoute({ getParentRoute: () => app, path: "/admin/indicateurs", component: IndicateursResultats });
const adminVeille = createRoute({ getParentRoute: () => app, path: "/admin/veille", component: AdminVeille });
const adminCpf = createRoute({ getParentRoute: () => app, path: "/admin/cpf", component: AdminCpf });
const financements = createRoute({ getParentRoute: () => app, path: "/financements", component: TableauFinancements });
const financement = createRoute({ getParentRoute: () => app, path: "/financements/$id", component: SuiviDemande });
const adminFinanceurs = createRoute({ getParentRoute: () => app, path: "/admin/financeurs", component: FichesFinanceurs });
const maVitrine = createRoute({ getParentRoute: () => app, path: "/ma-vitrine", component: MaVitrine });
const adminAudience = createRoute({ getParentRoute: () => app, path: "/admin/audience", component: AudienceSite });
const espaceAmbassadeur = createRoute({ getParentRoute: () => app, path: "/ambassadeur", component: EspaceAmbassadeur });
const adminAmbassadeurs = createRoute({ getParentRoute: () => app, path: "/admin/ambassadeurs", component: AdminAmbassadeurs });
const adminPaiementsAmbassadeurs = createRoute({ getParentRoute: () => app, path: "/admin/reglages/paiements-ambassadeurs", component: ReglagesPaiementsAmbassadeurs });
const adminGalerieStyles = createRoute({ getParentRoute: () => app, path: "/admin/galerie-styles", component: GalerieStyles });

const arbre = racine.addChildren([
  connexion,
  inscription,
  invitation,
  motDePasseOublie,
  reinitialisation,
  positionnementPublic,
  formulairePublic,
  recommandationPublique,
  app.addChildren([espaceAmbassadeur, adminAmbassadeurs, adminPaiementsAmbassadeurs, accueil, mesDossiers, nouveauDossier, arborescence, dossier, formations, formation, sourcesFormation, outils, repertoire, candidature, adminCandidatures, adminBoiteReception, adminOrganisme, bpf, courriers, compte, profil, coffres, coffre, positionnements, archives, modeEmploi, adminPilotage, adminIndicateurs, adminVeille, adminCpf, adminGalerieStyles, financements, financement, adminFinanceurs, maVitrine, adminAudience]),
]);

export const routeur = createRouter({ routeTree: arbre, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof routeur;
  }
}
